import React from 'react';
import { ArrowRightIcon, BookOpenIcon, CircleDollarSignIcon, FileTextIcon, LandmarkIcon, SearchXIcon } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { HeroA } from './HeroA';
import { UniversityRowCard } from './UniversityRowCard';
import { CompareBarA } from './CompareBarA';
import { ProgrammeResults } from '../sections/ProgrammeResults';
import { ScholarshipResults } from '../sections/ScholarshipResults';
import { FeedSection } from '../sections/FeedSection';
import { SavedSection } from '../sections/SavedSection';
import { ApplicationWorkspace } from '../ApplicationWorkspace';
import { UniversityCompareModal } from '../UniversityCompareModal';
import { useDiscover, type DiscoverSection } from '../../../contexts/DiscoverContext';
import type { SortMode } from '../../../utils/discover';
import { cn } from '../../../utils/cn';

/**
 * DESIGN A — the editorial discovery composition.
 *
 * Space, photography and one thing at a time. It answers "where could I go"
 * before it answers "how do I narrow this down", so the hero and the institution
 * cards carry the weight and refinement stays light.
 *
 * It renders the shared Discover state. It owns no data of its own.
 */
const QUICK_ACTIONS: {
  section: DiscoverSection;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  tone: string;
}[] = [{
  section: 'Universities',
  title: 'Explore universities',
  description: 'Discover institutions',
  icon: LandmarkIcon,
  tone: 'bg-brand-50 text-brand-700'
}, {
  section: 'Programmes',
  title: 'Find programmes',
  description: 'Browse what you could study',
  icon: BookOpenIcon,
  tone: 'bg-info-50 text-info-700'
}, {
  section: 'Scholarships',
  title: 'Find scholarships',
  description: 'Funding opportunities',
  icon: CircleDollarSignIcon,
  tone: 'bg-warn-50 text-warn-800'
}, {
  section: 'Applications',
  title: 'Track applications',
  description: 'Check your progress',
  icon: FileTextIcon,
  tone: 'bg-brand-50 text-brand-700'
}];
const SORTS: SortMode[] = ['Recommended', 'Most popular', 'Recently added'];
const SECONDARY: DiscoverSection[] = ['Feed', 'Saved', 'Compare'];
export function DesignA() {
  const {
    section,
    setSection,
    universities,
    sort,
    setSort,
    clearFilters,
    activeChips,
    savedCount,
    compared,
    compareOpen,
    setCompareOpen,
    toggleSave,
    isSaved
  } = useDiscover();
  return <div className="space-y-8" data-id="element-1082" data-mp="5eqtj723pgf8">
      <HeroA data-id="element-1083" data-mp="5eihfhdsyf53" />

      {/* Recommended for you --------------------------------------------- */}
      <section aria-labelledby="a-quick" data-id="element-1084" data-mp="af65bu6qifv3">
        <h2 id="a-quick" className="text-body font-semibold text-ink" data-id="element-1085" data-mp="ac0h0h5xji0f">
          Recommended for you
        </h2>
        <ul className="mt-3 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-1086" data-mp="1lt9xcutrhtxi">
          {QUICK_ACTIONS.map(action => {
          const Icon = action.icon;
          const active = section === action.section;
          return <li key={action.section} data-id="element-1087" data-mp="31diwo0a0bgmo">
                <button type="button" onClick={() => setSection(action.section)} aria-current={active ? 'page' : undefined} className={cn('group flex w-full items-center gap-3.5 rounded-card border bg-surface px-4 py-4 text-left transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(15,23,42,0.07)]', active ? 'border-brand-700' : 'border-line hover:border-brand-200')} data-id="element-1088" data-mp="31ekk7487lvla">
                  <span aria-hidden className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', action.tone)} data-id="element-1089" data-mp="28pn2p3bto2tl">
                    <Icon className="h-5 w-5" data-id="element-1090" data-mp="ewvici3b49yy" />
                  </span>
                  <span className="min-w-0" data-id="element-1091" data-mp="jw85z5205l2l">
                    <span className="block text-body font-semibold text-ink" data-id="element-1092" data-mp="jux1f30qg9af">{action.title}</span>
                    <span className="block truncate text-body-sm text-ink-muted" data-id="element-1093" data-mp="4y5ywkvrihgl">
                      {action.description}
                    </span>
                  </span>
                </button>
              </li>;
        })}
        </ul>

        <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1094" data-mp="x3vrsyq5l9c">
          {SECONDARY.map(item => <button key={item} type="button" onClick={() => setSection(item)} aria-current={section === item ? 'page' : undefined} className={cn('min-h-touch rounded-lg px-3.5 text-body-sm font-medium transition-colors duration-150 ease-out', section === item ? 'bg-brand-50 text-brand-800' : 'text-ink-muted hover:bg-canvas hover:text-ink')} data-id="element-1095" data-mp="3ie3xgu6ngvpz">
              {item}
              {item === 'Saved' && savedCount > 0 && ` (${savedCount})`}
              {item === 'Compare' && compared.length > 0 && ` (${compared.length})`}
            </button>)}
        </div>
      </section>

      {/* Universities ------------------------------------------------------ */}
      {section === 'Universities' && <section aria-labelledby="a-universities" className="space-y-4" data-id="element-1096" data-mp="3dmzu49uscolf">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-line pb-1" data-id="element-1097" data-mp="38pq5uxj4uiz6">
            <h2 id="a-universities" className="text-h3 font-semibold tracking-tight text-ink" data-id="element-1098" data-mp="3mh4p95gycm6r">
              Universities for you
            </h2>
            <div className="cz-tabstrip flex items-center gap-1" role="group" aria-label="Order results" data-id="element-1099" data-mp="a6habtepoqg">
              {SORTS.map(mode => <button key={mode} type="button" onClick={() => setSort(mode)} aria-pressed={sort === mode} className={cn('relative min-h-touch shrink-0 px-3 text-body-sm font-medium transition-colors duration-150 ease-out', sort === mode ? 'text-brand-800 after:absolute after:inset-x-2 after:bottom-0 after:h-[3px] after:rounded-full after:bg-brand-700' : 'text-ink-muted hover:text-ink')} data-id="element-1100" data-mp="3riam9fhnavm3">
                  {mode}
                </button>)}
            </div>
            {activeChips.length > 0 && <button type="button" onClick={clearFilters} className="ml-auto inline-flex min-h-touch items-center gap-1.5 text-body-sm font-medium text-brand-700 hover:text-brand-800" data-id="element-1101" data-mp="3gsocgevlz3yc">
                View all
                <ArrowRightIcon aria-hidden className="h-4 w-4" data-id="element-1102" data-mp="1rft89xda4eaf" />
              </button>}
          </div>

          {activeChips.length > 0 && <p className="text-body-sm text-ink-muted" data-id="element-1103" data-mp="1f51wrcr0k8yt">
              Narrowed by {activeChips.map(chip => chip.label).join(', ')} —{' '}
              {universities.length} of 6 institutions.
            </p>}

          {universities.length === 0 ? <div className="rounded-card border border-line bg-surface" data-id="element-1104" data-mp="155b1h8umppxo">
              <EmptyState icon={<SearchXIcon className="h-5 w-5" data-id="element-1106" data-mp="1xvtbyuxomtl4" />} title="No institutions match what you asked for" description="Nothing has been hidden — no published institution fits every filter at once. Clearing one of them will bring the list back." action={<Button variant="secondary" onClick={clearFilters} data-id="element-1107" data-mp="hhl486hkmotj">
                    Clear all filters
                  </Button>} data-id="element-1105" data-mp="22u4nda3mlc5g" />
            </div> : <ul className="space-y-4" data-id="element-1108" data-mp="2ckpui9jyhb7w">
              {universities.map(university => <li key={university.id} data-id="element-1109" data-mp="2cn2hx28hgdsc">
                  <UniversityRowCard university={university} data-id="element-1110" data-mp="2hkvp9ylivdu7" />
                </li>)}
            </ul>}

          <p className="text-caption text-ink-subtle" data-id="element-1111" data-mp="23lw1et5u32z3">
            Marks beside each institution are Campuzon identifiers, not official crests. Campus
            photography is representative of the setting and is not supplied by the institution.
          </p>
        </section>}

      {section === 'Programmes' && <section aria-label="Programmes" className="space-y-4" data-id="element-1112" data-mp="30c4zyb7148nd">
          <h2 className="text-h3 font-semibold tracking-tight text-ink" data-id="element-1113" data-mp="2mie1bxuu696f">Programmes for you</h2>
          <ProgrammeResults density="editorial" data-id="element-1114" data-mp="2rgp8u1xns7lh" />
        </section>}

      {section === 'Scholarships' && <section aria-label="Scholarships" className="space-y-4" data-id="element-1115" data-mp="3f69wgy4rbt13">
          <h2 className="text-h3 font-semibold tracking-tight text-ink" data-id="element-1116" data-mp="3jzcqf7dg54ht">Funding you could apply for</h2>
          <ScholarshipResults density="editorial" data-id="element-1117" data-mp="3tc1tr9qyip20" />
        </section>}

      {section === 'Applications' && <ApplicationWorkspace data-id="element-1118" data-mp="3paq79d3dqjy1" />}

      {section === 'Feed' && <FeedSection data-id="element-1119" data-mp="6x8we9fcu2zc" />}

      {section === 'Saved' && <SavedSection data-id="element-1120" data-mp="6x0l2lyvr5q9" />}

      {section === 'Compare' && <section aria-label="Comparison" className="space-y-4" data-id="element-1121" data-mp="1xxeuy75ytgp">
          <h2 className="text-h3 font-semibold tracking-tight text-ink" data-id="element-1122" data-mp="20igbm3jv7dx">Side by side</h2>
          {compared.length === 0 ? <div className="rounded-card border border-line bg-surface" data-id="element-1123" data-mp="36cx9sqbiz281">
              <EmptyState icon={<SearchXIcon className="h-5 w-5" data-id="element-1125" data-mp="3kcy941c561le" />} title="Nothing selected to compare" description="Add up to three institutions from the universities list and they will be held here while you keep browsing." action={<Button variant="primary" onClick={() => setSection('Universities')} data-id="element-1126" data-mp="zo0rf3kooll3">
                    Browse universities
                  </Button>} data-id="element-1124" data-mp="36f05nqzczpko" />
            </div> : <>
              <Button variant="primary" onClick={() => setCompareOpen(true)} data-id="element-1127" data-mp="2kgtdso3lo5hx">
                Open the comparison
              </Button>
              <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1128" data-mp="2fojrdi1p1nke">
                {compared.map(university => <li key={university.id} className="rounded-card border border-line bg-surface p-5" data-id="element-1129" data-mp="2apfg8lv7lfrf">
                    <p className="text-body font-semibold text-ink" data-id="element-1130" data-mp="21psc3sb3hvuo">{university.name}</p>
                    <p className="mt-1 text-body-sm text-ink-muted" data-id="element-1131" data-mp="1wsjy83wzcdz8">
                      {university.city}, {university.country}
                    </p>
                    <Badge className="mt-3" plain data-id="element-1132" data-mp="3r5jfe1h0dg8m">
                      {university.type}
                    </Badge>
                    <div className="mt-4" data-id="element-1133" data-mp="3m5x6234awk53">
                      <Button size="sm" variant={isSaved('university', university.id) ? 'secondary' : 'ghost'} onClick={() => toggleSave('university', university.id)} data-id="element-1134" data-mp="3m787hwa9xft1">
                        {isSaved('university', university.id) ? 'Saved' : 'Save'}
                      </Button>
                    </div>
                  </li>)}
              </ul>
            </>}
        </section>}

      {section !== 'Compare' && <CompareBarA data-id="element-1135" data-mp="3fq6rkqx4c7z1" />}

      <UniversityCompareModal open={compareOpen} onClose={() => setCompareOpen(false)} selected={compared} data-id="element-1136" data-mp="3fpesh64bbs40" />
    </div>;
}